// src/components/Eventos/TrackEvents.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Button,
  TableCell,
  Typography,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import LaneSortableItem from './LaneSortableItem';
import ActionColumn, { ActionColumnHeader } from './ActionColumn';
import ResultModal from './ResultModal';
import DownloadTrackLiffButton from './DownloadTrackLiffButton';
import { useEventContext } from '../../context/EventContext';
import { parseTime } from '../../utils/ResultUtils';
import { formatWithPrecision }  from '../../utils/precision';
import UploadLifButton from './UploadLifButton';
import { isSpeedEvent } from '../../utils/eventUtils';
import { computeGlobalPlaces } from '../../utils/globalRankings';


const TrackEvents = ({ heatDetails, editingResults, editingByLane }) => {
  const theme = useTheme();
  // Detección de tamaño de pantalla
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // <900px
  
  // Configuración de estilos responsivos basados en tamaño de pantalla
  const responsiveStyles = useMemo(() => ({
    tableCell: {
      padding: isMobile ? '4px 2px' : isTablet ? '6px 4px' : '16px 8px',
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
    },
    button: {
      minWidth: isMobile ? '40px' : isTablet ? '60px' : '80px',
      padding: isMobile ? '2px 4px' : isTablet ? '4px 8px' : '8px 16px',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.75rem' : '0.875rem',
    },
    inputField: {
      width: isMobile ? '60px' : isTablet ? '80px' : '120px',
      '& .MuiInputBase-input': {
        fontSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
        padding: isMobile ? '6px 8px' : isTablet ? '8px 10px' : '10px 12px',
      }
    },
    typography: {
      nameSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
      teamSize: isMobile ? '0.6rem' : isTablet ? '0.65rem' : '0.75rem'
    },
    iconButton: {
      padding: isMobile ? '2px' : isTablet ? '4px' : '8px',
    },
    iconSize: isMobile ? '0.8rem' : isTablet ? '1rem' : '1.2rem',
    windButton: {
      minWidth: isMobile ? '60px' : isTablet ? '90px' : '120px',
    }
  }), [isMobile, isTablet]);
  
  const {
    updateParticipantResults,
    updateHeatParticipants,
    getParticipantResults,
    calculateRankings,
    isTrackEventFn,
    pplData,
    precision,
    rowPrecisions,
    updateRowPrecision,  
    updateHeatWind,
    participantsResults,
    getTestSettings,
    saveSeedMarks,
    getSeedMarks,
    getHeatWind,
    getPlace,
    hasPassed,
  } = useEventContext();

  const [lanes, setLanes] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openResultModal, setOpenResultModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const heatKey = `${heatDetails.eventNumber}_${heatDetails.round}_${heatDetails.heat}`;
  const { eventCode, eventNumber, round, heat } = heatDetails;
  const { enablePoints, enableWind } = getTestSettings(eventNumber, round);
  const [openSeedMarkModal, setOpenSeedMarkModal] = useState(false);
  const [selectedSeedParticipant, setSelectedSeedParticipant] = useState(null);
  const [seedMarks, setSeedMarks] = useState({});
  const [heatWindInput, setHeatWindInput] = useState('');

  // 1) Participantes planos
  const participantsArray = Array.isArray(heatDetails.participants)
    ? heatDetails.participants
    : [];

  const globalPlaces = useMemo(() =>
    computeGlobalPlaces(
      participantsResults, 
      heatDetails.eventNumber, 
      heatDetails.round,
      heatDetails.eventCode
    ),
    [participantsResults, heatDetails.eventNumber, heatDetails.round, heatDetails.eventCode]
  );

  // 2.1) Handler "seguro" para añadir/reemplazar inline
  const handleInlineAdd = useCallback(async (laneNumber, newIdNumber) => {
    const trimmed = newIdNumber.trim();
    // 1) ¿ya existe en este heat?
    if (participantsArray.some(p => p.idNumber === trimmed)) {
      setSnackbar({
        open: true,
        message: `El atleta ${trimmed} ya está en este heat.`,
        severity: 'error'
      });
      return;
    }
    // 2) ¿existe en tu base de atletas?
    const found = pplData.find(p => p.idNumber === trimmed);
    if (!found) {
      setSnackbar({
        open: true,
        message: `Atleta ${trimmed} no encontrado.`,
        severity: 'error'
      });
      return;
    }
    // 3) Añadimos normalmente
    updateHeatParticipants(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      found.idNumber,
      laneNumber,
      'add',
      { ...found, lane: laneNumber }
    );
    setSnackbar({
      open: true,
      message: `${found.fullName} añadido al carril ${laneNumber}.`,
      severity: 'success'
    });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  }, [participantsArray, pplData, heatDetails, updateHeatParticipants, calculateRankings]);

  // 2) Cálculo dinámico de carriles
  const lanesCount = useMemo(() => {
    const numericLanes = participantsArray
      .map(p => parseInt(p.lane, 10))
      .filter(n => !isNaN(n));
    const maxAssigned = numericLanes.length ? Math.max(...numericLanes) : 0;
    return Math.max(maxAssigned, participantsArray.length);
  }, [participantsArray]);

  // 3) Poblamos el estado `lanes`
  useEffect(() => {
    const arr = Array(lanesCount).fill(null);
    const resultsData = participantsResults[heatKey] || {};
    let idx = 0;

    participantsArray.forEach(p => {
      const id = String(p.idNumber);
      const res = resultsData[id] || {};
      const merged = {
        ...p,
        ...res,
        idNumber: id,
        wind: res.wind || '',
        notes: res.notes || '',
        seedMark: p.seedMark || '',
        points: res.points || 0
      };
      const laneNum = parseInt(merged.lane, 10);
      if (!isNaN(laneNum) && laneNum >= 1 && laneNum <= lanesCount) {
        arr[laneNum - 1] = merged;
      } else {
        while (idx < lanesCount && arr[idx] !== null) idx++;
        if (idx < lanesCount) arr[idx++] = merged;
      }
    });

    setLanes(arr);
  }, [participantsArray, lanesCount, participantsResults, heatKey]);

  useEffect(() => {
    const loadSeedMarks = async () => {
      const marks = {};
      for (const participant of participantsArray) {
        if (participant.idNumber) {
          try {
            const seedMarkData = await getSeedMarks(participant.idNumber);
            if (seedMarkData && seedMarkData[eventCode]?.seedMark) {
              marks[participant.idNumber] = seedMarkData[eventCode].seedMark;
            }
          } catch (error) {
            console.error(`Error cargando marca de siembra para ${participant.idNumber}:`, error);
          }
        }
      }
      setSeedMarks(marks);
    };
    
    if (participantsArray.length > 0) {
      loadSeedMarks();
    }
  }, [participantsArray, eventCode, getSeedMarks]);

  const [openWindModal, setOpenWindModal] = useState(false);

  const handleSaveHeatWind = () => {
    updateHeatWind(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      heatWindInput
    );
    setSnackbar({ open:true, message:`Viento del heat guardado: ${heatWindInput}`, severity:'success' });
    setOpenWindModal(false);
  };

  useEffect(() => {
    const loadWind = async () => {
      if (heatDetails) {
        try {
          // Opción 1: Obtener directamente del documento del heat
          const windValue = getHeatWind(
            heatDetails.eventNumber,
            heatDetails.round,
            heatDetails.heat
          );
          
          console.log('Valor de viento cargado:', windValue); // Para debug
          setHeatWindInput(windValue || '');
          
          // Opción 2 (alternativa): Revisar directamente en los resultados
          if (!windValue) {
            const heatKey = `${heatDetails.eventNumber}_${heatDetails.round}_${heatDetails.heat}`;
            const resultsData = participantsResults[heatKey] || {};
            
            // Buscar el viento en cualquier participante
            for (const participantId in resultsData) {
              if (resultsData[participantId].wind) {
                console.log('Valor de viento encontrado en participante:', resultsData[participantId].wind);
                setHeatWindInput(resultsData[participantId].wind);
                break;
              }
            }
          }
        } catch (error) {
          console.error('Error al cargar el viento del heat:', error);
        }
      }
    };
    
    loadWind();
  }, [heatDetails, getHeatWind, participantsResults]);

  // Handle notes update
  const handleNotesUpdate = (participantId, notes) => {
    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      { notes }
    );
    setSnackbar({ open: true, message: 'Notas guardadas.', severity: 'success' });
  };

  // 5a) Añadir un nuevo carril
  const handleAddLane = () => {
    const used = lanes.map(l => l?.lane).filter(n => !!n).map(Number);
    let next = 1;
    while (used.includes(next)) next++;
    setLanes(prev => [...prev, null]);
    setSnackbar({ open:true, message:`Carril añadido.`, severity:'success' });
  };

  // 5b) Añadir un atleta en un carril vacío (vía prompt)
  const handleAddAthlete = useCallback((laneNumber) => {
    const idNum = window.prompt('Ingrese el número de atleta (idNumber):');
    if (!idNum) return;
    const found = pplData.find(p => p.idNumber === idNum.trim());
    if (!found) {
      setSnackbar({ open: true, message: `Atleta con idNumber ${idNum} no encontrado.`, severity: 'error' });
      return;
    }
    if (participantsArray.some(p => p.idNumber === found.idNumber)) {
      setSnackbar({ open: true, message: `${found.fullName} ya está en este heat.`, severity: 'warning' });
      return;
    }
    updateHeatParticipants(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      found.idNumber,
      laneNumber,
      'add',
      { ...found, lane: laneNumber }
    );
    setSnackbar({ open: true, message: `${found.fullName} añadido al carril ${laneNumber}.`, severity: 'success' });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  }, [
    pplData,
    participantsArray,
    heatDetails,
    updateHeatParticipants,
    calculateRankings
  ]);

  // 5c) Reemplazar inline un atleta existente
  const handleReplaceAthlete = useCallback(async (laneNumber, newIdNumber) => {
    const found = pplData.find(p => p.idNumber === newIdNumber);
    if (!found) {
      setSnackbar({ open: true, message: `Atleta ${newIdNumber} no encontrado.`, severity: 'error' });
      return;
    }
    const current = lanes[laneNumber - 1];
    if (current) {
      updateHeatParticipants(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        current.idNumber,
        null,
        'remove'
      );
    }
    updateHeatParticipants(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      found.idNumber,
      laneNumber,
      'add',
      { ...found, lane: laneNumber }
    );
    setSnackbar({
      open: true,
      message: `${found.fullName} asignado al carril ${laneNumber}.`,
      severity: 'success'
    });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  }, [
    pplData,
    lanes,
    heatDetails,
    updateHeatParticipants,
    calculateRankings
  ]);

  // Handle seed mark update
  const handleSeedMarkUpdate = (participantId, seedMark) => {
    updateHeatParticipants(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      null,
      'update',
      { seedMark }
    );
    setSnackbar({ open: true, message: 'Marca de siembra guardada.', severity: 'success' });
  };

  const handleDeleteSeedMark = (participantId) => {
    try {
      // Guardar marca de siembra con valor vacío (esto efectivamente la "elimina")
      saveSeedMarks(participantId, eventCode, "");
      
      // Actualizar el estado local
      setSeedMarks(prevMarks => {
        const newMarks = { ...prevMarks };
        delete newMarks[participantId]; // Eliminar la entrada del mapa
        return newMarks;
      });
      
      // Actualizar la UI
      setLanes(currentLanes => {
        return currentLanes.map(lane => {
          if (lane && lane.idNumber === participantId) {
            return {
              ...lane,
              seedMark: ""
            };
          }
          return lane;
        });
      });
      
      // Cerrar el modal
      setOpenSeedMarkModal(false);
      setSelectedSeedParticipant(null);
      
      setSnackbar({ 
        open: true, 
        message: "Marca de siembra eliminada", 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error al eliminar marca de siembra:', error);
      setSnackbar({ 
        open: true, 
        message: `Error al eliminar marca: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  // 6) Gestión del modal de resultados
  const handleOpenResultModal = athlete => {
    setSelectedParticipant(athlete);
    setOpenResultModal(true);
  };
  const handleCloseResultModal = () => {
    setSelectedParticipant(null);
    setOpenResultModal(false);
  };

  const handleSaveResult = (participantId, newResult) => {
    // Esperamos newResult tenga: { time, wind, special, reactionTime, lapTimes, notes }
    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      newResult
    );
    setSnackbar({ open: true, message: 'Resultado guardado correctamente.', severity: 'success' });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
    handleCloseResultModal();
  };
  const handleSpecialAction = (participantId, specialStatus) => {
    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      { special: specialStatus }
    );
    setSnackbar({ open: true, message: `Opción especial: ${specialStatus}`, severity: 'info' });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  };
  const handleDeleteResult = participantId => {
    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      { time: '', wind: '', reactionTime: null, lapTimes: [] }
    );
    setSnackbar({ open: true, message: 'Resultado eliminado.', severity: 'info' });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
    handleCloseResultModal();
  };

  const handleSaveSeed = async (idNumber, seedMark) => {
    try {
      await saveSeedMarks(idNumber, eventCode, seedMark);
      
      // Actualizar el estado local de marcas de siembra
      setSeedMarks(prevMarks => ({
        ...prevMarks,
        [idNumber]: seedMark
      }));
      
      // Actualizar la UI localmente - actualiza el array de lanes
      setLanes(currentLanes => {
        return currentLanes.map(lane => {
          if (lane && lane.idNumber === idNumber) {
            return {
              ...lane,
              seedMark: seedMark
            };
          }
          return lane;
        });
      });
  
      // También actualiza el participante seleccionado para mantener consistencia con el modal
      if (selectedSeedParticipant && selectedSeedParticipant.idNumber === idNumber) {
        setSelectedSeedParticipant({
          ...selectedSeedParticipant,
          seedMark: seedMark
        });
      }
      
      setOpenSeedMarkModal(false);
      setSnackbar({ 
        open: true, 
        message: `Marca de siembra guardada: ${seedMark}`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error al guardar marca de siembra:', error);
      setSnackbar({ 
        open: true, 
        message: `Error al guardar marca: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  // 7) Drag & Drop
  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = lanes.findIndex((lane, idx) =>
      lane ? String(lane.idNumber) === active.id : `empty-${idx + 1}` === active.id
    );
    const newIdx = lanes.findIndex((lane, idx) =>
      lane ? String(lane.idNumber) === over.id : `empty-${idx + 1}` === over.id
    );
    if (oldIdx < 0 || newIdx < 0) return;
    const newLanes = [...lanes];
    const moved = newLanes[oldIdx];
    newLanes[oldIdx] = newLanes[newIdx];
    newLanes[newIdx] = moved;
    newLanes.forEach((ath, idx) => {
      if (ath) {
        updateHeatParticipants(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat,
          ath.idNumber,
          idx + 1
        );
      }
    });
    setLanes(newLanes);
    setSnackbar({ open: true, message: 'Carriles actualizados.', severity: 'success' });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  };

  // 8) Items para SortableContext
  const items = lanes.map((lane, idx) => (lane ? String(lane.idNumber) : `empty-${idx + 1}`));

  // 9) Renderizado de celdas extras (ahora con estilos responsivos)
  const renderAdditionalCells = athlete => {
    if (!athlete) {
      // Return empty cells for all columns when no athlete
      return (
        <>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
          {enablePoints && <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>}
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
          <TableCell align="center" sx={responsiveStyles.tableCell}>{editingByLane ? '-' : null}</TableCell>
        </>
      );
    }

    // Lee la precisión de esta fila, o usa la global si no hay una local
    const prec = rowPrecisions[heatKey]?.[athlete.idNumber] ?? precision;
    const secs = parseTime(athlete.time || '');
    const label = isNaN(secs)
      ? '-'
      : formatWithPrecision(secs, prec);
      
    // Get global place (across all heats)
    const globalPlace = globalPlaces[athlete.idNumber] ?? '-';

    return (
      <>
        {/* Marca de siembra */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectedSeedParticipant(athlete);
              setOpenSeedMarkModal(true);
            }}
            sx={{
              ...responsiveStyles.button,
              color: 'success.main',
              borderColor: 'success.main',
              fontWeight: 'bold',
              '&:hover': {
                borderColor: 'success.dark',
                backgroundColor: 'success.light',
                opacity: 0.9,
              }
            }}
          >
            {seedMarks[athlete.idNumber] || athlete.seedMark || '—'}
          </Button>
        </TableCell>

        {/* Tiempo / especial */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          <Button 
            variant="outlined" 
            sx={{
              ...responsiveStyles.button,
              fontWeight: 'bold',
              borderColor: 'primary.main',
              '&:hover': {
                borderColor: 'success.dark',
                backgroundColor: 'primary.light',
                opacity: 0.9,
              }
            }}
            onClick={() => handleOpenResultModal(athlete)}
          >
            {athlete.special || label}
          </Button>
        </TableCell>

        {/* Lugar en el heat */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          {getPlace(
            heatDetails.eventNumber,
            heatDetails.round,
            heatDetails.heat,
            athlete.idNumber
          )}
        </TableCell>

        {/* Lugar global (en todos los heats) */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          {globalPlace}
        </TableCell>

        {/* Puntos (solo si está habilitado) */}
        {enablePoints && (
          <TableCell align="center" sx={responsiveStyles.tableCell}>
            {athlete.points || '-'}
          </TableCell>
        )}

        {/* Notas */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          <TextField
            size="small"
            value={athlete.notes || ''}
            onChange={(e) => handleNotesUpdate(athlete.idNumber, e.target.value)}
            variant="outlined"
            placeholder="Notas"
            sx={responsiveStyles.inputField}
          />
        </TableCell>

        {/* Menú Estado / Precisión / Eliminar */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          <ActionColumn
            participant={athlete}
            editingResults={editingResults}
            handleEditLane={(upd, newLane) => {
              updateHeatParticipants(
                heatDetails.eventNumber,
                heatDetails.round,
                heatDetails.heat,
                upd.id,
                parseInt(newLane, 10),
                'update'
              );
              setSnackbar({ open: true, message: 'Carril actualizado.', severity: 'success' });
            }}
            handleRemoveAthlete={rem => {
              updateHeatParticipants(
                heatDetails.eventNumber,
                heatDetails.round,
                heatDetails.heat,
                rem.idNumber,
                null,
                'remove'
              );
              setSnackbar({ open: true, message: 'Atleta eliminado.', severity: 'info' });
            }}
            handleActionSelect={spec =>
              handleSpecialAction(athlete.idNumber, spec)
            }
            // Pasa la callback que actualiza solo esta fila
            handlePrecisionSelect={precValue => {
              updateRowPrecision(heatKey, athlete.idNumber, precValue);
            }}
            isTrackEvent={isTrackEventFn(heatDetails.eventCode)}
            responsiveStyles={responsiveStyles}
          />
        </TableCell>
      </>
    );
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      { isSpeedEvent(eventCode) && enableWind && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
          <Typography 
            variant={isMobile ? "subtitle2" : "h6"} 
            component="div"
            sx={{ fontSize: responsiveStyles.typography.nameSize }}
          >
            {isMobile ? "V" : "Viento"}
          </Typography>
          <Button
            variant="outlined"
            color={heatWindInput ? "success" : "secondary"}
            onClick={() => {
              setOpenWindModal(true);
            }}
            sx={{
              ...responsiveStyles.button,
              ...responsiveStyles.windButton,
              ...(heatWindInput ? { 
                fontWeight: 'bold',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                border: '1px solid',
                borderColor: 'success.main'
              } : {})
            }}
          >
            {heatWindInput ? `${heatWindInput} m/s` : (isMobile ? "+ V" : "+ Viento")}
          </Button>
        </Box>
      )}
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <TableContainer 
            component={Paper}
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[300],
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <Table stickyHeader size={isMobile ? "small" : isTablet ? "medium" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Mv" : "Mover"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "C" : "Carril"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Num" : "Número"}</TableCell>
                  <TableCell align="left" sx={responsiveStyles.tableCell}>{isMobile ? "Nombre" : "Nombre y Equipo"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Siemb" : "Siembra"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "T" : "Tiempo"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "L" : "Lugar"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "L.Gen" : "Lugar Gen"}</TableCell>
                  {enablePoints && <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Pts" : "Puntos"}</TableCell>}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Not" : "Notas"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>
                    <ActionColumnHeader
                      handleAddAthlete={handleAddLane}
                      responsiveStyles={responsiveStyles}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lanes.map((ath, idx) => {
                  const laneNumber = idx + 1;
                  const isPassed = ath && hasPassed(
                    heatDetails.eventNumber,
                    heatDetails.round,
                    heatDetails.heat,
                    ath.idNumber
                  );
                  const rowStyle = isPassed
                    ? { backgroundColor: '#c8e6c9' }
                    : idx % 2 === 0
                      ? { backgroundColor: theme.palette.action.hover }
                      : {};

                  return (
                    <LaneSortableItem
                      key={ath ? `ath-${ath.idNumber}` : `empty-${laneNumber}`}
                      laneNumber={laneNumber}
                      athlete={ath}
                      onAddAthlete={handleAddAthlete}
                      onReplaceAthlete={handleInlineAdd}
                      additionalCells={renderAdditionalCells(ath)}
                      rowStyle={rowStyle}
                      editingByLane={editingByLane}
                      cellStyle={responsiveStyles.tableCell}
                      buttonStyle={responsiveStyles.button}
                      typographyStyle={responsiveStyles.typography}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <DownloadTrackLiffButton
          heatDetails={heatDetails}
          getParticipantResults={getParticipantResults}
        />
        <UploadLifButton heatDetails={heatDetails} />
      </Box>

      <ResultModal
        open={openResultModal}
        onClose={handleCloseResultModal}
        participant={selectedParticipant}
        eventType={heatDetails.eventCode}
        isTrackEvent={isTrackEventFn(heatDetails.eventCode)}
        onSave={handleSaveResult}
        onDelete={handleDeleteResult}
        windEnabled={enableWind}
        precision={precision}
        // 10) Inicializamos el modal con todos los campos:
        initialValues={{
          time: selectedParticipant?.time || '',
          wind: selectedParticipant?.wind || '',
          reactionTime: selectedParticipant?.reactionTime || '',
          lapTimes: selectedParticipant?.lapTimes || [],
          notes: selectedParticipant?.notes || '',
        }}
      />
      <ResultModal
        open={openWindModal}
        onClose={() => setOpenWindModal(false)}
        heatWindMode={true}
        heatKey={heatKey}
        initialWind={heatWindInput}
        onSaveWind={(_, wind)=>{      //  ←  NUEVO nombre
          updateHeatWind(
            heatDetails.eventNumber,
            heatDetails.round,
            heatDetails.heat,
            wind
          );
          setHeatWindInput(wind);
          setSnackbar({ open:true, message:`Viento del heat: ${wind}`, severity:'success' });
          setOpenWindModal(false);
        }}
      />
      <ResultModal
        open={openSeedMarkModal}
        onClose={() => {
          setSelectedSeedParticipant(null);
          setOpenSeedMarkModal(false);
        }}
        participant={selectedSeedParticipant}
        eventType={heatDetails.eventCode}
        isTrackEvent={isTrackEventFn(heatDetails.eventCode)}
        seedMarkMode={true} // activamos el modo especial
        onSaveSeedMark={handleSaveSeed}
        onDelete={handleDeleteSeedMark}
        initialValues={{
          seedMark: selectedSeedParticipant ? 
          (seedMarks[selectedSeedParticipant.idNumber] || 
           selectedSeedParticipant.seedMark || 
           '') : '',
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrackEvents;